// ============================================================================
// Bounded fetch for the production Supabase client (browser).
// ============================================================================
// supabase-js sets no request deadline of its own, and browser fetch does not
// either — a dropped connection or an edge that accepts the request but never
// answers leaves the awaited call pending forever. A stalled request can leave
// a caller waiting indefinitely. boundedFetch.test.ts pins this transport's
// deadline and cancellation behavior.
//
// This wraps the Supabase client's transport with an AbortController-based
// deadline so every request is guaranteed to settle (resolve or reject) within
// its timeout. That turns a silent, indefinite hang into an ordinary rejected
// promise, which route-level error handling can surface instead of leaving the
// page indefinitely pending.
//
// The deadline is NOT one fixed number for every request: a real Storage
// object upload/update (a rural/mobile photo or document, not a PostgREST
// query) can legitimately take well over 20 seconds to finish sending bytes,
// and a single ordinary-request ceiling would abort a slow-but-healthy
// upload exactly like a stalled one. resolveRequestTimeoutMs classifies the
// request and gives Storage upload mutations a longer, separately documented
// budget while every other request (Auth, PostgREST, Storage reads/list/
// sign/move/copy) keeps the short one.
//
// Deliberately NOT shared with tests/setup/supabaseTransport.js: that module
// bounds the Node-side (undici) TEST fixture transport with its own
// diagnostic env-var override for CI attribution. This module targets the
// browser production client and needs none of that.
// ============================================================================

// Comfortably above any real PostgREST/Auth round trip (including the largest
// paginated boot queries) and comfortably below a user's patience for a
// visible "Loading" state before an explicit error + Retry is preferable.
export const DEFAULT_SUPABASE_REQUEST_TIMEOUT_MS = 20_000;

// Storage file uploads/updates send request bytes over the wire (not just a
// small JSON query), and rural/mobile connections can legitimately take well
// over 20s for a multi-MB photo or document. 120s is generous enough for that
// without being indefinite — a genuinely stalled upload still fails closed
// and surfaces a recoverable error instead of hanging forever.
export const STORAGE_UPLOAD_TIMEOUT_MS = 120_000;

// Matches only the Storage object-upload/update routes (POST/PUT to
// /storage/v1/object/<bucket>/<path>, and the resumable-upload signed-URL PUT
// at /storage/v1/object/upload/sign/...), never the metadata/read-side calls
// that also live under /object/ — list and list-v2 (paginated bucket
// listings), sign (create a signed URL), info (object metadata), move, and
// copy are all comparatively small/fast and stay on the ordinary deadline.
const STORAGE_UPLOAD_PATH_RE = /^\/storage\/v1\/object\/(?!list\/|list-v2\/|move$|copy$|sign\/|info\/|public\/)/;

// Exactly what a WHATWG fetch (and therefore supabase-js's `global.fetch`
// override) is called with: a URL string, a Request, or a URL object, plus the
// init bag.
//
// Both are derived from the RUNTIME `fetch` binding rather than written as
// lib.dom's `RequestInfo`/`RequestInit`. Those two are TYPE-ONLY names with no
// runtime binding, and ESLint's no-undef resolves identifiers against a list of
// runtime globals, so naming them directly is reported as undefined. The rule
// cannot simply be switched off for TypeScript here: the observer-integrity
// gate (tests/static/tooling_extension_discovery_static.test.js) asserts that
// no-undef fires for every source extension, and uses that as its proof the
// linter actually observes each one. Deriving from `typeof fetch` sidesteps the
// conflict and is strictly more accurate anyway — these are, by construction,
// the parameter types of the function being wrapped.
export type FetchInput = Parameters<typeof fetch>[0];
export type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;

export type FetchImpl = (input: FetchInput, init?: FetchInit) => Promise<Response>;

// Classifies one request into a deadline. Swappable so a test can pin a
// deterministic budget without fabricating request shapes.
export type TimeoutResolver = (input: FetchInput, init?: FetchInit) => number;

export type BoundedFetchOptions = {
  fetchImpl?: FetchImpl;
  timeoutMs?: number;
  resolveTimeoutMs?: TimeoutResolver;
  now?: () => number;
};

// `Partial<Request>` is the narrowest honest description of the probes below: a
// Request carries `.method` and `.url`, a URL object carries neither, and a
// plain string is handled by its own branch. Asserting the specific optional
// shape keeps these two helpers from reaching for `any`.
function requestMethod(input: FetchInput, init?: FetchInit | null): string {
  const raw = init?.method || (typeof input === 'object' && input && (input as Partial<Request>).method) || 'GET';
  return String(raw).toUpperCase();
}

function requestUrl(input: FetchInput): string | null {
  const candidate = input as Partial<Request>;
  return typeof input === 'string' ? input : typeof candidate?.url === 'string' ? candidate.url : null;
}

export function isStorageUploadRequest(input: FetchInput, init?: FetchInit | null): boolean {
  const method = requestMethod(input, init);
  if (method !== 'POST' && method !== 'PUT') return false;
  const rawUrl = requestUrl(input);
  if (!rawUrl) return false;
  let pathname;
  try {
    pathname = new URL(rawUrl).pathname;
  } catch {
    return false;
  }
  return STORAGE_UPLOAD_PATH_RE.test(pathname);
}

// The default classifier: Storage upload/update mutations get the long
// budget, everything else (Auth, PostgREST, Storage reads/list/sign/move/
// copy) gets the ordinary one.
export function resolveRequestTimeoutMs(input: FetchInput, init?: FetchInit): number {
  return isStorageUploadRequest(input, init) ? STORAGE_UPLOAD_TIMEOUT_MS : DEFAULT_SUPABASE_REQUEST_TIMEOUT_MS;
}

// One link in a `cause` chain, declared recursively so the walk below can follow
// `.cause` without an assertion at every hop. The single assertion is where the
// opaque caught value enters.
export type ErrorChainNode = {message?: string; cause?: ErrorChainNode} | null | undefined;

// undici/browser fetch nest the real fault as `cause`; surfacing only the top
// message (e.g. plain "Failed to fetch") discards the actual reason. Flatten
// the chain so a diagnostic log has something a person can act on. (Never
// rendered to the boot UI directly — see src/lib/bootDataLoad.js.)
export function describeFetchError(error: unknown): string {
  const seen = new Set<unknown>();
  const parts: string[] = [];
  let cur = error as ErrorChainNode;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const msg = cur.message || String(cur);
    parts.push(msg);
    cur = cur.cause;
  }
  return parts.join(' <- ');
}

// Wraps a fetch implementation with a bounded, per-request deadline.
// `fetchImpl` and `now` are injectable so the timing behavior is
// unit-testable with a controllable clock instead of a real degraded
// network. `timeoutMs` (a fixed number) overrides classification entirely —
// mainly for tests that want one deterministic deadline regardless of
// request shape. Production omits it and lets `resolveTimeoutMs` classify
// each request individually.
export function createBoundedFetch({
  fetchImpl = (...args: Parameters<FetchImpl>) => fetch(...args),
  timeoutMs,
  resolveTimeoutMs = resolveRequestTimeoutMs,
  now = () => Date.now(),
}: BoundedFetchOptions = {}): FetchImpl {
  return async function boundedFetch(input: FetchInput, init: FetchInit = {}): Promise<Response> {
    const effectiveTimeoutMs = timeoutMs != null ? timeoutMs : resolveTimeoutMs(input, init);
    const started = now();
    const controller = new AbortController();
    // Preserve any caller-supplied signal: either aborting wins.
    const callerSignal = init.signal;
    const onCallerAbort = () => controller.abort(callerSignal?.reason);
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort(callerSignal.reason);
      else callerSignal.addEventListener('abort', onCallerAbort, {once: true});
    }
    const timer = setTimeout(() => controller.abort(new Error('deadline')), effectiveTimeoutMs);
    try {
      return await fetchImpl(input, {...init, signal: controller.signal});
    } catch (error) {
      const elapsed = now() - started;
      // Caller-initiated aborts (e.g. a component unmounting) are not a
      // transport stall — do not relabel them as a timeout.
      if (callerSignal?.aborted) throw error;
      if (controller.signal.aborted) {
        throw new Error(
          `Supabase request timed out after ${effectiveTimeoutMs}ms (elapsed ${elapsed}ms). ` +
            `The request never received a response.`,
        );
      }
      throw new Error(`Supabase request failed after ${elapsed}ms: ${describeFetchError(error)}`);
    } finally {
      clearTimeout(timer);
      callerSignal?.removeEventListener?.('abort', onCallerAbort);
    }
  };
}
